import { Dialog, DialogContent, DialogHeader, DialogTitle, ConfirmDialog } from "@tellescope/ui";
import { Spinner, Button } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import type { Form, FormResponse } from "@tellescope/types-models";
import { useState } from "react";
import {
  usePinnedFormDialogShallowStore,
  selectHasPendingFiles,
  EnduserFormWizard,
  type EnduserFormWizardProps,
} from "@tellescope/forms";
import { useOrganization } from "../../../shared/store/organizationStore";
import { useEnduserCurrentEnduser, useEnduserStore } from "../../store/hooks";

interface NativePinnedFormDialogProps {
  form: Form | null;
  existingResponse: FormResponse | null;
  isPreparingForm: boolean;
  error: string | null;
  accessCode: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NativePinnedFormDialog = ({
  form,
  existingResponse,
  isPreparingForm,
  error,
  accessCode,
  open,
  onOpenChange,
}: NativePinnedFormDialogProps) => {
  const { t } = useTranslation("forms");
  const currentEnduser = useEnduserCurrentEnduser();
  const session = useEnduserStore((state) => state.session);
  const organization = useOrganization();
  const { pendingFilesByFieldId, reset: resetFormStore } = usePinnedFormDialogShallowStore(
    "pendingFilesByFieldId",
    "reset"
  );
  const hasPendingFiles = selectHasPendingFiles(pendingFilesByFieldId);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const [chainedForm, setChainedForm] = useState<{
    formId: string;
    responseId: string;
    accessCode: string;
  } | null>(null);

  const formTitle = form?.displayTitle || form?.title || t("Form");
  const enduserId = currentEnduser?.id ?? existingResponse?.enduserId ?? "";

  if (!currentEnduser || !accessCode) {
    return null;
  }

  const continueToFormInApp: EnduserFormWizardProps["continueToFormInApp"] = (prepared) => {
    if (!prepared.accessCode) return false;
    // A new form begins here, so the previous one's queued files must not carry over.
    resetFormStore();
    setChainedForm({ formId: prepared.formId, responseId: prepared.id, accessCode: prepared.accessCode });
    return true;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && hasPendingFiles) {
          setIsConfirmingDiscard(true);
          return;
        }
        if (!nextOpen) {
          resetFormStore();
          setChainedForm(null);
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="w-[90svw] max-w-[650px] sm:max-w-[650px] h-[85dvh] flex flex-col p-0 gap-0"
        disableCloseOnClickOutside
      >
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="h-8 w-8 p-0 md:hidden"
              onClick={() => onOpenChange(false)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle className="text-base font-medium truncate pr-8">{formTitle}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isPreparingForm ? (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <Spinner size="sm">{t("Loading form...")}</Spinner>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-destructive">
                <p className="text-sm font-medium mb-1">{t("Error loading form")}</p>
                <p className="text-xs text-muted-foreground">{error}</p>
              </div>
            </div>
          ) : form ? (
            <EnduserFormWizard
              formArgs={
                chainedForm
                  ? {
                      formId: chainedForm.formId,
                      responseId: chainedForm.responseId,
                      accessCode: chainedForm.accessCode,
                      enduserId,
                      enduser: currentEnduser,
                    }
                  : {
                      formId: form.id,
                      formTitle: form.displayTitle || form.title,
                      responseId: existingResponse?.id,
                      enduserId,
                      parentResponseId: existingResponse?.parentResponseId,
                      rootResponseId: existingResponse?.rootResponseId,
                      existingResponses: existingResponse?.responses,
                      accessCode: accessCode ?? undefined,
                      publicIdentifier: existingResponse?.publicIdentifier,
                      enduser: currentEnduser,
                    }
              }
              surface="pinned-form-dialog"
              onComplete={() => onOpenChange(false)}
              session={session}
              organizationSubdomain={organization?.subdomain}
              organization={organization ?? undefined}
              continueToFormInApp={continueToFormInApp}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center p-8 h-full">
              <div className="text-center text-muted-foreground">
                <p className="text-sm">{t("Unable to load form")}</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      <ConfirmDialog
        open={isConfirmingDiscard}
        onOpenChange={setIsConfirmingDiscard}
        variant="warning"
        title={t("Discard selected files?")}
        description={t("You selected files that haven't been uploaded yet. Closing this form will discard them.")}
        confirmText={t("Discard and close")}
        cancelText={t("Keep editing")}
        onConfirm={() => {
          setIsConfirmingDiscard(false);
          resetFormStore();
          setChainedForm(null);
          onOpenChange(false);
        }}
      />
    </Dialog>
  );
};
